import { useEffect, useMemo, useState } from "react"
import { Button, FormInstance } from "antd"
import { Button as CustomButton } from "components"
import { debounce } from "lodash"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { ewaAxiosInstance } from "utils/request"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import type { IWorkplaceByIdRes, WorkplaceFormValues } from "../typings"
import { SETUP_WORKPLACE_QUERY_KEY } from "../typings"
import { useWorkplacesList } from "../hooks/useWorkplacesList"
import { useWorkplaceSetupProgress } from "../hooks/useWorkplaceSetupProgress"
import { useWorkplaceDrawer } from "../hooks/useWorkplaceDrawer"
import { getResumeStepFromWorkplaceConfig } from "../utils/workplaceSetupResumeStep"
import CustomTAndAWrapper from "../components/CustomTAndAWrapper"
import StepsMainLayout from "./components/StepsMainLayout"
import SetupSteps from "../components/SetupSteps"
import SetupSummary from "../components/SetupSummary"
import SetupShiftPlans from "./components/SetupShiftPlans"
import SetupDutyLocations from "./components/SetupDutyLocations"
import SetupWorkplaceSelection from "./components/SetupWorkplaceSelection"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { ChevronLeftIcon } from "@heroicons/react/24/outline"
import { onQueryError } from "utils/utils"
import { toast } from "react-toastify"
import WorkplaceFormDrawer from "./components/WorkplaceEditDrawer"

// ─── Constants ────────────────────────────────────────────────────────────────

const SETUP_STEPS = [
  "Workplace selection",
  "Duty locations",
  "Shift plans",
  "Summary & publish",
] as const

const FAQ_DISMISSED_KEY = "setup_faq_dismissed"

// ─── Types ────────────────────────────────────────────────────────────────────

interface TaxOffice {
  description: string
  code: string
}

// ─── Component ────────────────────────────────────────────────────────────────

const Setup = () => {
  const [selectedWorkplaceId, setSelectedWorkplaceId] = useState("")
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [currentStep, setCurrentStep] = useState(0)
  const [isSavingProgress, setIsSavingProgress] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const workplaceIdFromUrl = searchParams.get("workplaceId") ?? ""
  const openCreateWorkplaceFromUrl = searchParams.get("createWorkplace") === "1"

  const [showFAQs, setShowFAQs] = useState(!openCreateWorkplaceFromUrl)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ── Search ─────────────────────────────────────────────────────────────────

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value)
        setPage(1)
      }, 500),
    []
  )

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  // ── Data ───────────────────────────────────────────────────────────────────

  const { workplaces, total, isWorkplacesFetching, workplacesQueryParams } =
    useWorkplacesList({ page, search })

  const selectedWorkplace = workplaces.find((w) => w.id === selectedWorkplaceId)

  const selectedWorkplaceHasCoordinates =
    selectedWorkplace?.latitude != null && selectedWorkplace?.longitude != null

  const { setupProgressRes, isLoadingSetupProgress } =
    useWorkplaceSetupProgress({
      selectedWorkplaceId,
      currentStep,
      workplaceIdFromUrl,
      onResumeStep: setCurrentStep,
    })

  const {
    createWorkplaceDrawerOpen,
    configureAddressWorkplace,
    createWorkplaceForm,
    openConfigureAddressDrawer,
    openCreateWorkplaceDrawer,
    openEditWorkplaceDrawer,
    closeCreateWorkplaceDrawer,
    handleDrawerFormFinish,
    isCreatingWorkplace,
    isUpdatingWorkplace,
  } = useWorkplaceDrawer({ setCurrentStep, workplacesQueryParams })

  // Pre-select workplace from URL on first load
  useEffect(() => {
    if (workplaceIdFromUrl && !selectedWorkplaceId) {
      setSelectedWorkplaceId(workplaceIdFromUrl)
    }
    if (!openCreateWorkplaceFromUrl) return
    setCurrentStep(0)
    openCreateWorkplaceDrawer()

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("createWorkplace")
    setSearchParams(nextParams, { replace: true })
    // setShowFAQs(false)
  }, [
    workplaceIdFromUrl,
    selectedWorkplaceId,
    openCreateWorkplaceFromUrl,
    openCreateWorkplaceDrawer,
    searchParams,
    setSearchParams,
  ])

  // ── Mutation ──────────────────────────────────────────────────

  const { mutate: toDraft, isLoading: isSavingToDraft } = useMutation({
    mutationFn: async ({
      status,
      id,
    }: {
      status: "draft" | "published"
      id: number
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/workplace-config/${id}`,
        { status }
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success("Draft Workplace successfully")
      console.log("Data -> ", data)
      queryClient.invalidateQueries({
        queryKey: [`/v1/time-attendance/workplaces?${workplacesQueryParams}`],
      })
      navigate("/dashboard/time-and-attendance/overview")
    },
    onError: onQueryError,
  })

  // ── Tax / housing offices ──────────────────────────────────────────────────

  const [alpha2Code, setAlpha2Code] = useState("")
  const [stateCode, setStateCode] = useState("")

  const { isLoading: isTaxOfficesLoading, data: taxOfficesRes } = useQuery<
    IBaseRes<TaxOffice[]>
  >({
    queryKey: [COMPANY_ENDPOINTS.GET_TAX_OFFICES, stateCode],
    queryFn: () => companyService.getTaxOffices(stateCode),
    enabled: !!stateCode,
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })
  const taxOffices = taxOfficesRes?.data

  const { isLoading: isHousingLocationsLoading, data: housingLocationsRes } =
    useQuery<IBaseRes<TaxOffice[]>>({
      queryKey: [COMPANY_ENDPOINTS.GET_HOUSING_LOCATIONS, stateCode],
      queryFn: () => companyService.getHousingLocations(stateCode),
      enabled: !!stateCode,
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      keepPreviousData: true,
    })
  const housingLocations = housingLocationsRes?.data

  // ── Form value change handler ──────────────────────────────────────────────

  const handleFormValuesChange = (
    changedValues: Partial<WorkplaceFormValues>
  ) => {
    if ("country" in changedValues) {
      setAlpha2Code(changedValues.country ?? "")
      setStateCode("")
      createWorkplaceForm.setFieldsValue({
        state: "",
        taxOffice: undefined,
        taxOfficeCode: "",
        nhfOffice: undefined,
        nhfOfficeCode: "",
      })
    }

    if ("state" in changedValues) {
      setStateCode(changedValues.state ?? "")
      createWorkplaceForm.setFieldsValue({
        taxOffice: undefined,
        taxOfficeCode: "",
        nhfOffice: undefined,
        nhfOfficeCode: "",
      })
    }

    if ("taxOffice" in changedValues) {
      const match = taxOffices?.find(
        (t) => t.description === changedValues.taxOffice
      )
      createWorkplaceForm.setFieldsValue({ taxOfficeCode: match?.code ?? "" })
    }

    if ("nhfOffice" in changedValues) {
      const match = housingLocations?.find(
        (h) => h.description === changedValues.nhfOffice
      )
      createWorkplaceForm.setFieldsValue({ nhfOfficeCode: match?.code ?? "" })
    }
  }

  // ── Navigation helpers ─────────────────────────────────────────────────────

  const { mutate: trackWorkplaceConfigMutate, isLoading: isTrackingProgress } =
    useMutation({
      mutationFn: (updates: Record<string, boolean>) =>
        ewaAxiosInstance.put(
          `/v1/time-attendance/workplace-config/${selectedWorkplaceId}`,
          updates
        ),
    })

  const trackWorkplaceConfig = (
    updates: Record<string, boolean>
  ): Promise<boolean | undefined> => {
    return new Promise((resolve) => {
      if (!selectedWorkplaceId || !selectedWorkplaceHasCoordinates) {
        resolve(undefined)
        return
      }
      trackWorkplaceConfigMutate(updates, {
        onSuccess: (res) => {
          resolve(res.data?.success ? true : undefined)
        },
        onError: () => {
          // Tracking errors are non-blocking
          resolve(undefined)
        },
      })
    })
  }

  const advanceStep = async (
    nextStep: number,
    updates?: Record<string, boolean>
  ) => {
    setIsSavingProgress(true)
    try {
      if (updates) await trackWorkplaceConfig(updates)
      setCurrentStep(nextStep)
    } finally {
      setIsSavingProgress(false)
    }
  }

  const handleWorkplaceSelectionNext = async () => {
    setIsSavingProgress(true)
    try {
      if (!selectedWorkplaceId || !selectedWorkplaceHasCoordinates) {
        setCurrentStep(1)
        return
      }

      const workplaceConfig =
        (setupProgressRes as IWorkplaceByIdRes)?.data?.workplaceConfig ??
        selectedWorkplace?.workplaceConfig

      if (workplaceConfig == null) {
        await ewaAxiosInstance.post("/v1/time-attendance/workplace-configs", {
          workplaceId: selectedWorkplaceId,
        })
      }

      await trackWorkplaceConfig({ workplaceSelected: true })

      const targetStep = workplaceConfig
        ? getResumeStepFromWorkplaceConfig(
            workplaceConfig as Record<string, unknown>
          )
        : 1

      await advanceStep(targetStep)
    } finally {
      setIsSavingProgress(false)
    }
  }

  const handleDismissFAQs = () => {
    setShowFAQs(false)
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <CustomTAndAWrapper>
      <StepsMainLayout
        wrapperClassName="ta-setup-lock flex flex-col gap-2 h-full overflow-hidden lg:flex-row"
        mainClassName="flex-1 min-w-0 min-h-0 bg-white rounded-md py-4 px-4 md:py-6 md:px-6 flex flex-col overflow-hidden relative"
        sidebar={
          <SetupSteps
            steps={SETUP_STEPS}
            currentStep={currentStep}
          />
        }
        isBusy={
          isSavingProgress || (!!selectedWorkplaceId && isLoadingSetupProgress)
        }
      >
        {currentStep === 0 && (
          <SetupWorkplaceSelection
            workplaces={workplaces}
            selectedWorkplaceId={selectedWorkplaceId}
            selectedWorkplaceHasCoordinates={selectedWorkplaceHasCoordinates}
            onSelect={setSelectedWorkplaceId}
            onConfigureAddress={openConfigureAddressDrawer}
            page={page}
            total={total}
            onPageChange={setPage}
            onCreateNewWorkplaceClick={openCreateWorkplaceDrawer}
            onEditWorkplaceClick={openEditWorkplaceDrawer}
            onBack={() => navigate("/dashboard/time-and-attendance/overview")}
            onNext={handleWorkplaceSelectionNext}
            isConfiguringWorkplace={isUpdatingWorkplace}
            isSavingProgress={isSavingProgress}
            isLoading={isWorkplacesFetching}
            searchValue={searchInput}
            onSearchChange={handleSearchChange}
            configureAddressWorkplace={configureAddressWorkplace}
            createWorkplaceDrawerOpen={createWorkplaceDrawerOpen}
            closeCreateWorkplaceDrawer={closeCreateWorkplaceDrawer}
            taxOffices={taxOffices ?? []}
            isTaxOfficesLoading={isTaxOfficesLoading}
            housingLocations={housingLocations ?? []}
            isHousingLocationsLoading={isHousingLocationsLoading}
            isCreatingWorkplace={isCreatingWorkplace}
            isUpdatingWorkplace={isUpdatingWorkplace}
            alpha2Code={alpha2Code}
          />
        )}

        {currentStep === 1 && (
          <SetupDutyLocations
            workplaceId={selectedWorkplaceId}
            onBack={() => setCurrentStep(0)}
            onNext={() => advanceStep(2, { dutyLocationSelected: true })}
            isSavingProgress={isSavingProgress}
          />
        )}

        {currentStep === 2 && (
          <SetupShiftPlans
            onBack={() => setCurrentStep(1)}
            onNext={() => advanceStep(3, { shiftPlansConfirmed: true })}
            workplaceId={
              selectedWorkplaceId ? Number(selectedWorkplaceId) : undefined
            }
            onTrackWorkplaceConfig={trackWorkplaceConfig}
            isTrackingProgress={isTrackingProgress}
            isSavingProgress={isSavingProgress}
            saveToDraft={() =>
              toDraft({ status: "draft", id: Number(selectedWorkplaceId) })
            }
            isDrafting={isSavingToDraft}
          />
        )}

        {currentStep === 3 && (
          <SetupSummary
            onBack={() => setCurrentStep(2)}
            workplaceId={selectedWorkplaceId}
            onEditWorkplace={openEditWorkplaceDrawer}
            saveToDraft={() =>
              toDraft({ status: "draft", id: Number(selectedWorkplaceId) })
            }
            isDrafting={isSavingToDraft}
          />
        )}
      </StepsMainLayout>

      <WorkplaceFormDrawer
        open={createWorkplaceDrawerOpen}
        isEditingAddress={!!configureAddressWorkplace}
        onClose={closeCreateWorkplaceDrawer}
        form={createWorkplaceForm}
        onFinish={handleDrawerFormFinish}
        onValuesChange={handleFormValuesChange}
        taxOffices={taxOffices ?? []}
        isTaxOfficesLoading={isTaxOfficesLoading}
        housingLocations={housingLocations ?? []}
        isHousingLocationsLoading={isHousingLocationsLoading}
        isCreatingWorkplace={isCreatingWorkplace}
        isUpdatingWorkplace={isUpdatingWorkplace}
        alpha2Code={alpha2Code}
      />
      <SetupFAQModal
        open={showFAQs}
        onClose={handleDismissFAQs}
      />
    </CustomTAndAWrapper>
  )
}

export default Setup

// ─── FAQ Modal ────────────────────────────────────────────────────────────────

const SetupFAQModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => (
  <CustomModal
    rootClassName="ta-scope"
    open={open}
    width={800}
    title="Workplaces & duty locations"
    titleClassName="text-black font-bold text-[0.875rem] leading-[150%]"
    onCancel={onClose}
    footer={
      <CustomButton
        color="primary"
        className="font-semibold w-full h-10"
        onClick={onClose}
      >
        Close
      </CustomButton>
    }
  >
    <div className="flex flex-col justify-center items-center gap-2 mb-4">
      <img
        src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/f2718b63-d290-4140-975b-d25b54eb2d0c.png"
        alt="Workplaces & duty locations"
        className="w-[200px] h-[140px] object-contain"
        width={200}
        height={140}
      />
      <p className="font-bold text-lg text-slate-900">
        Workplaces &amp; duty locations
      </p>
      <p className="text-sm text-slate-500">
        You're about to setup workplaces and duty locations, here are some
        useful definitions.
      </p>
    </div>

    <div className="flex flex-col gap-2">
      <FAQCard title="Workplaces">
        A workplace is a company branch that runs its own payroll, with its own
        employees and duty locations. Its branch address automatically becomes
        the main duty location, and you can add extra duty locations (like
        client sites) where employees work. Clock-in/out rules are set per
        location, not at the branch level.
      </FAQCard>
      <FAQCard title="Duty Locations">
        A duty location is where employees physically show up and clock in.
        Every workplace gets one automatically (its branch address), and you can
        add extras — like client offices or project sites — each with its own
        work schedules and GPS boundaries.
      </FAQCard>
    </div>
  </CustomModal>
)

const FAQCard = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <div className="border-2 border-[#E2E8F0] rounded-md p-4 flex flex-col gap-2">
    <p className="text-black font-bold text-[0.875rem] leading-[150%]">
      {title}
    </p>
    <p className="text-slate-500">{children}</p>
  </div>
)

export const CustomBackButton = ({ onBack }: { onBack: () => void }) => {
  return (
    <Button
      type="default"
      className="self-start font-semibold px-4 py-2 h-auto rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] flex items-center gap-2"
      icon={<ChevronLeftIcon className="w-4 h-4" />}
      onClick={onBack}
    >
      Go back
    </Button>
  )
}
